import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Button, CheckBox, Modal, Select, TextInput, Theme } from '../../../components';
import { Store } from '../../../models';
import { departmentTemplatesSelectors, storeActions, storeSelectors, userSelectors } from '../../../redux';

import './AddEditStore.scss';

interface Props {
  isOpen: boolean;
  closeModal: () => void;
  store: Store;
}

const AddEditStore: FC<Props> = ({ isOpen, closeModal, store }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const isAddEditLoading = useSelector(storeSelectors.isAddEditLoading);
  const departmentTemplates = useSelector(departmentTemplatesSelectors.getDepartmentTemplates);
  const userInfo = useSelector(userSelectors.getUserInfo);

  const [selection, setSelection] = useState<string[]>([]);
  const [storeExternalId, setStoreExternalId] = useState<string>('');
  const [storeName, setStoreName] = useState<string>('');
  const [languageCode, setLanguageCode] = useState<string>('');
  const [isValid, setIsValid] = useState<boolean>(false);

  useEffect(() => {
    setSelection((store?.departments || []).map(department => department.departmentTemplate.id));
    setStoreExternalId(store?.externalId || '');
    setStoreName(store?.name || '');
    setLanguageCode(store?.defaultLanguage.code || '');
  }, [store, setSelection, setStoreExternalId, setStoreName, setLanguageCode]);

  const handleCloseModal = () => {
    setStoreExternalId('');
    setStoreName('');
    setLanguageCode('');
    setSelection([]);
    setIsValid(false);
    closeModal();
  };

  useEffect(() => {
    if (isValid && !isAddEditLoading) {
      handleCloseModal();
    }
  }, [isAddEditLoading]);

  useEffect(() => {
    if (storeExternalId && storeName && selection.length && languageCode) {
      setIsValid(true);
    } else {
      setIsValid(false);
    }
  }, [selection, storeExternalId, storeName, languageCode]);

  const onCheck = id => {
    let newArray = [...selection];
    if (newArray.find(s => s === id)) {
      newArray = newArray.filter(s => s !== id);
    } else {
      newArray.push(id);
    }
    setSelection(newArray);
  };

  const renderCheckBox = departmentTemplate => {
    return (
      <CheckBox
        key={`checkbox_${departmentTemplate.id}`}
        value={departmentTemplate.id}
        name={departmentTemplate.name}
        onChange={onCheck}
        isChecked={!!selection.find(s => s === departmentTemplate.id)}
      />
    );
  };

  const save = () => {
    const newStore = {
      name: storeName,
      externalId: storeExternalId,
      defaultCountryLanguageCode: languageCode,
      departments: selection.map(s => ({
        id: s,
      })),
    };

    if (store) dispatch(storeActions.editStore(store.id, newStore));
    else dispatch(storeActions.addStore(newStore));
  };

  return (
    <Modal title={t(store ? 'lblEditStore' : 'lblAddStore')} isOpen={isOpen} closeModal={() => closeModal()}>
      <div className='add-edit-container'>
        <TextInput label={t('lblStoreId')} placeholder={t('phStoreId')} onChange={setStoreExternalId} value={storeExternalId} />
        <TextInput label={t('lblStoreName')} placeholder={t('phStoreName')} onChange={setStoreName} value={storeName} />
        <Select
          label='lblStoreLanguage'
          placeholder='phStoreLanguage'
          options={userInfo.languages.map(({ code, language }) => ({
            value: code,
            label: language,
          }))}
          onChange={setLanguageCode}
          value={languageCode}
        />

        <p className='small'>{t('lblDepartments')}</p>
        <div className='department-scrollable'>{departmentTemplates.map(renderCheckBox)}</div>

        <div className='buttons'>
          <Button theme={Theme.INVERSE} onClickCallback={handleCloseModal}>
            {t('lblCancel')}
          </Button>
          <Button theme={Theme.SECONDARY} onClickCallback={save} isLoading={isAddEditLoading} isDisabled={!isValid}>
            {t('lblSave')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AddEditStore;
